import { IGetListYearGroupService } from "../../../../../abstractions/services/actions/IGetListYearGroupService";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import { MonitoreoPlagaEnfermedadItem } from "../../../../../generated/grpc/catalogos_monitoreo_pb";
import {
  fx_createSesionSemanaGroupListByFincaIdService,
  SemanaMonitoreoItem,
} from "../../../../../modulos/monitoreo/sesion/services/SesionSemanaGroupListByFincaIdService";
import { BaseSecondSidebarItemRender } from "../../../../base/sidebar/BaseSecondSidebarItemRender";
import { BaseSecondSidebarRender } from "../../../../base/sidebar/BaseSecondSidebarRender";
import { MonitoreoSesionSemanaItemRender } from "../list/MonitoreoSesionSemanaItemRender";
import {
  ConteoEstados,
  SeleccionPlagas,
} from "../../../../../modulos/monitoreo/sesion/semaforo";
import { ESTADO_COLOR } from "../dashboard/monitoreoAnalisisPaleta";

/** Contador incremental de instancias, para no colisionar el id del botón del
 *  pie entre sidebars vivos. */
let SIDEBAR_ID = 0;

/** Segundo sidebar de la vista de sesión: las semanas de monitoreo de la finca
 *  activa, agrupadas por año. Al seleccionar una, el mapa pinta sus lotes.
 *
 *  Sin botón "+" ni vista dividida: el monitoreo se captura en la app móvil,
 *  acá solo se lee. */
export class MonitoreoSesionSecondSidebarRender extends BaseSecondSidebarRender<SemanaMonitoreoItem> {
  /**
   * @param onBack     Acción del botón volver.
   * @param _onSemana  Notifica la semana y el recorrido elegidos.
   * @param _getSeleccion  Plagas activas (`null` = modo "Todas").
   * @param _getPlagasById Catálogo completo, que el modo "Todas" necesita.
   * @param _onAnalisis Acción del botón fijo al fondo, que lleva al dashboard.
   */
  constructor(
    onBack: () => void,
    private readonly _onSemana: (
      semana: SemanaMonitoreoItem,
      sesionId: string | null,
    ) => void,
    private readonly _getSeleccion: () => SeleccionPlagas,
    private readonly _getPlagasById: () => Map<
      string,
      MonitoreoPlagaEnfermedadItem
    >,
    private readonly _onAnalisis: () => void,
    private readonly _onVerInforme: (
      semana: SemanaMonitoreoItem,
      sesionId: string | null,
    ) => void,
  ) {
    super(onBack);
    SIDEBAR_ID += 1;
    this._footerId = SIDEBAR_ID;
  }

  /** El HTML de la base más un pie propio: leyenda del semáforo y acceso al
   *  dashboard de análisis.
   *
   *  El pie se agrega acá y no con un hook de `BaseSecondSidebarRender` para
   *  no tocar la base. Funciona porque el string que devuelve `render()` se
   *  inyecta entero en `#secondSidebarWrapper`, que es un flex column: el pie
   *  queda como hermano de la lista y, al no encogerse, se ancla abajo mientras
   *  la lista scrollea.
   *
   *  Va en el sidebar y no sobre el mapa porque ahí la barra de plagas puede
   *  ocupar varias filas —una finca con muchas plagas, u otro cultivo— y el
   *  botón se perdería entre ellas.
   *
   *  El acceso al **informe** no vive acá sino dentro de cada semana, junto a
   *  sus fechas: el informe es de una semana concreta (o de un día concreto),
   *  así que un botón fijo al pie no podría decir de cuál. El del pie lleva al
   *  dashboard, que sí es de toda la finca. */
  override render(): string {
    return `${super.render()}
      <div class="mon-ses-footer">
        ${_renderLeyenda(this._footerId, null)}
        <button class="sx-btn sx-btn-secondary sx-btn-block" id="monSesAnalisis-${this._footerId}">
          <i class="ph ph-chart-line"></i>
          Dashboard
        </button>
      </div>`;
  }

  /** Repinta el contenido de los items vivos. La llama la página cuando cambia
   *  la plaga activa: el item expandido tiene que releer sus valores o queda
   *  contando una plaga distinta de la que pinta el mapa.
   *
   *  Recorre un registro propio porque la lista de items de
   *  `BaseSecondSidebarRender` es privada. No hizo falta abrirla: esta subclase
   *  es quien los crea, así que puede quedárselos. */
  refreshItems(): void {
    this._itemsPropios.forEach((item) => item.refresh());
  }

  /** Actualiza los números de la leyenda: cuántos lotes hay de cada color.
   *
   *  La leyenda y el contador son lo mismo dicho en dos mitades —qué significa
   *  el color y cuántos hay—, por eso viven en la misma fila en vez de ser dos
   *  bloques. El conteo lo calcula la página, que es quien conoce la selección
   *  activa y el listado completo de lotes de la finca.
   *
   *  `null` es "todavía no se puede contar" —el catálogo de plagas no llegó— y
   *  deja los números en blanco en vez de escribir ceros. Un "sin monitorear 4"
   *  transitorio diría que la finca entera está sin recorrer, que es la lectura
   *  que más se cuida en este módulo. */
  setConteo(conteo: ConteoEstados | null): void {
    const leyenda = document.getElementById(`monSesLeyenda-${this._footerId}`);
    if (!leyenda) return;
    leyenda.outerHTML = _renderLeyenda(this._footerId, conteo);
  }

  /** Descarta el registro de items del año anterior.
   *
   *  La base llama a esto justo antes de crear los items del año elegido, así
   *  que es el punto exacto donde los anteriores dejan de existir; sin el
   *  reset, cambiar de año acumularía referencias a items ya dispuestos. */
  protected override filterItems(
    items: SemanaMonitoreoItem[],
  ): SemanaMonitoreoItem[] {
    this._itemsPropios = [];
    return items;
  }

  override connect(): void {
    super.connect();
    this._analisisBtn = document.getElementById(
      `monSesAnalisis-${this._footerId}`,
    );
    this._analisisBtn?.addEventListener("click", this._onAnalisis);
  }

  override dispose(): void {
    this._analisisBtn?.removeEventListener("click", this._onAnalisis);
    this._analisisBtn = null;
    super.dispose();
  }

  protected get title(): string {
    return "MONITOREO DE PLAGAS";
  }

  /** Semanas de la finca activa, agrupadas por año. */
  protected getYearGroupListService(): IGetListYearGroupService<SemanaMonitoreoItem> {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    return fx_createSesionSemanaGroupListByFincaIdService(finca.id);
  }

  /** Id de instancia para el id del botón del pie. Lleva nombre propio porque
   *  `BaseSecondSidebarRender` ya tiene un `_sidebarId` privado. */
  private readonly _footerId: number;
  /** Botón del pie (disponible tras `connect`). */
  private _analisisBtn: HTMLElement | null = null;

  /** Item de semana (lotes monitoreados y su valor en las plagas activas). */
  protected createItem(
    item: SemanaMonitoreoItem,
  ): BaseSecondSidebarItemRender {
    const render = new MonitoreoSesionSemanaItemRender(
      item,
      this._onSemana,
      this._getSeleccion,
      this._getPlagasById,
      this._onVerInforme,
    );
    this._itemsPropios.push(render);
    return render;
  }

  /** Items creados por este sidebar, para poder repintarlos. */
  private _itemsPropios: MonitoreoSesionSemanaItemRender[] = [];
}

/** Leyenda del semáforo.
 *
 *  Va al pie del sidebar y no sobre el mapa por dos razones: arriba a la
 *  derecha están los botones de medición y zoom, y sumarla al panel superior
 *  agrandaría justo lo que ya obliga a las pills a ocupar varias filas. Acá
 *  explica de una sola vez los polígonos **y** los badges de la lista, que
 *  usan los mismos colores.
 *
 *  El cuarto renglón es el que justifica la leyenda entera: un lote que no se
 *  monitoreó se dibuja transparente y, sin explicación, se lee como un error de
 *  carga. Es la distinción que más se cuida en la capa de datos —`leerIncidencia`
 *  devolviendo `null` en vez de `0`— y era la única que no se decía en pantalla.
 *
 *  Los cuadros usan el color sólido (`ESTADO_COLOR`, el de los badges) y no el
 *  relleno traslúcido del polígono: sobre el fondo blanco del sidebar, el 55%
 *  de opacidad que el mapa necesita para dejar ver la imagen satelital se
 *  vuelve un pastel que no se distingue. */
function _renderLeyenda(id: number, conteo: ConteoEstados | null): string {
  const filas: [string, string, number | null][] = [
    [ESTADO_COLOR.ok, "Sin presión", conteo?.ok ?? null],
    [ESTADO_COLOR.warn, "Precaución", conteo?.warn ?? null],
    [ESTADO_COLOR.alert, "Alerta", conteo?.alert ?? null],
    ["transparent", "Sin monitorear", conteo?.sinDatos ?? null],
  ];

  const items = filas
    .map(
      ([color, etiqueta, cantidad]) =>
        `<span class="mon-ses-leyenda-item">
          <span class="mon-ses-leyenda-color" style="background:${color}"></span>
          <span class="mon-ses-leyenda-texto">${etiqueta}</span>
          <span class="mon-ses-leyenda-num">${cantidad ?? ""}</span>
        </span>`,
    )
    .join("");

  return `<div class="mon-ses-leyenda" id="monSesLeyenda-${id}">${items}</div>`;
}
